import React, { memo, useCallback, useMemo, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconArrowsMove, IconTrash } from '@tabler/icons-react';
import Waveform from './Waveform';
import { MIN_SEGMENT_WIDTH } from '../timeUtils';
import TotalVideoSegmentDuration from './TotalVideoSegmentDuration';
import { getSegmentDisplayText, getSegmentDisplayTextWithIntroOutroPrefix, truncateDisplayText } from '../utils';
import VideoSegmentTrimHandles from './VideoSegmentTrimHandles';
import { useVideoSegmentDrag } from '../hooks/useVideoSegmentDrag';
import { useTrimHandleHover } from '../hooks/useTrimHandleHover';
import { useRemoveVideoSegment } from '../hooks/useRemoveVideoSegment';
import { useMenuVisibility, MENU_BREAKPOINTS_WITH_DRAG } from '../hooks/useMenuVisibility';
import { getAllCompositeSegments, getAllSegments, isClipInOriginalLanguage } from '@/player-config/src/utils/clipUtils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { VideoSegment } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/VideoSegment';
import { isSplitsInTimelineEnabled } from '@/stores/clip';
import { classnames } from '@/libs/utils';
import Badge from '@/components/atoms/Badge/Badge';
import { EffectType } from '@/player-config/src/types/RemotionPlayerConfigTypes/Animations/Effects';

function VideoSegmentComponent({
  segment,
  videoElementHeight,
  speakerTimelineContainerRef
}: Readonly<{
  segment: VideoSegment;
  videoElementHeight: number;
  speakerTimelineContainerRef?: React.RefObject<HTMLDivElement>;
}>) {
  const { clipData, clipId, fps } = useClipsContext();
  const {
    dragStart: videoSegmentDragStart,
    dragEnd: videoSegmentDragEnd,
    setDragStart: setVideoSegmentDragStart,
    setDragEnd: setVideoSegmentDragEnd,
    segmentWidth
  } = useVideoSegmentDrag(segment, fps);
  const { showTrimHandlesOnHover, segmentRef } = useTrimHandleHover();
  const removeVideoSegment = useRemoveVideoSegment(clipId, segment.id);
  const [showDragHandle, setShowDragHandle] = useState(false);
  const menuVisibility = useMenuVisibility(segmentRef, MENU_BREAKPOINTS_WITH_DRAG);

  const isTranslated = useMemo(() => {
    return !isClipInOriginalLanguage(clipData);
  }, [clipData]);

  const showTime = menuVisibility === 'all';
  const showTrash = !isTranslated && (menuVisibility === 'all' || menuVisibility === 'no-time');
  const showDrag =
    !isTranslated && (menuVisibility === 'all' || menuVisibility === 'no-time' || menuVisibility === 'drag-only');

  const isSplitsEnabled = useMemo(() => {
    return isSplitsInTimelineEnabled(clipData);
  }, [clipData]);

  const isMenuVisible = useMemo(() => {
    if (menuVisibility === 'none') {
      return false;
    }
    if (menuVisibility === 'all') {
      return true;
    }
    if (isTranslated && !showTrash && !showDrag) {
      return false;
    }
    return true;
  }, [menuVisibility, isTranslated, showTrash, showDrag]);

  const someCompositeHasZoomEffect = useMemo(() => {
    return getAllCompositeSegments(clipData).some(s => s.effects?.some(e => e.type === EffectType.Zoom));
  }, [clipData]);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: segment.id,
    disabled: false
  });

  const style = {
    transform: CSS.Transform.toString(transform ? { ...transform, scaleX: 1, scaleY: 1 } : null),
    transition: transition
  };

  const isIntroOrOutroSegment = useMemo(() => {
    return segment.id === 'intro' || segment.id === 'outro';
  }, [segment.id]);

  const isFirstSegment = useMemo(() => {
    return getAllSegments(clipData).findIndex(s => s.id === segment.id) === 0;
  }, [clipData, segment.id]);

  const isLastSegment = useMemo(() => {
    return getAllSegments(clipData).findIndex(s => s.id === segment.id) === getAllSegments(clipData).length - 1;
  }, [clipData, segment.id]);

  const videoPreviewParentContainerStyle = useMemo(() => {
    return {
      marginTop: someCompositeHasZoomEffect ? '40px' : undefined
    };
  }, [someCompositeHasZoomEffect]);

  // Merges dnd-kit's sortable ref with the trim hover ref so both systems track the same DOM node
  const setNodeRefForSegment = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      segmentRef.current = node;
    },
    [setNodeRef, segmentRef]
  );

  if (!isSplitsEnabled) {
    return null;
  }

  return (
    <div
      ref={setNodeRefForSegment}
      className={classnames(
        'group relative',
        isFirstSegment ? 'pr-[1.5px]' : isLastSegment ? 'pl-[1.5px]' : '',
        isDragging ? 'z-50 opacity-50' : ''
      )}
      style={{
        width: `max(calc(${segmentWidth}px * var(--timeline-zoom)),${MIN_SEGMENT_WIDTH}px)`,
        height: videoElementHeight ? `${videoElementHeight}px` : undefined,
        ...style
      }}
      data-testid={segment.id}
      onMouseDown={e => {
        if (isDragging) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onMouseEnter={() => setShowDragHandle(true)}
      onMouseLeave={() => setShowDragHandle(false)}
    >
      {showDragHandle && !isDragging && isMenuVisible && (
        <div className="absolute bottom-[8px] right-[10px] z-50 flex items-center gap-0 rounded-md bg-black px-1.5 py-1 text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover/composite-segment:opacity-100 group-hover:opacity-100">
          {showTime && (
            <div className="mr-1 text-xs text-white">
              <TotalVideoSegmentDuration
                segment={segment}
                dragStart={videoSegmentDragStart}
                dragEnd={videoSegmentDragEnd}
              />
            </div>
          )}
          {isSplitsEnabled && showDrag && (
            <div
              {...attributes}
              {...listeners}
              className="flex cursor-move items-center justify-center rounded p-1.5 text-white transition-all duration-200 hover:bg-white/10 active:cursor-grabbing"
              data-testid="composite-segment-drag-handle"
            >
              {!isIntroOrOutroSegment ? <IconArrowsMove size={12} className="text-white" /> : null}
            </div>
          )}
          {isSplitsEnabled && showTrash && (
            <div
              onClick={e => {
                e.stopPropagation();
                removeVideoSegment();
              }}
              className="group/delete flex cursor-pointer items-center justify-center rounded p-1.5 text-white transition-all duration-200 hover:bg-red-500/20 active:bg-red-500/30"
              data-testid="composite-segment-delete-button"
            >
              <IconTrash
                size={12}
                className="text-white transition-colors duration-200 group-hover/delete:text-red-500"
              />
            </div>
          )}
        </div>
      )}
      <div className="group/intro-outro relative flex h-full flex-col gap-1">
        <div
          className={classnames(
            'absolute inset-0 rounded-[7px] border-2 border-slate-200 group-hover/intro-outro:border-slate-800',
            showTrimHandlesOnHover ? 'border-slate-950' : ''
          )}
        ></div>
        <div
          className="group/chapter relative mx-[4px] mt-[4px] min-h-[30px] overflow-hidden rounded-[3px] bg-slate-200 hover:bg-slate-500"
          style={videoPreviewParentContainerStyle}
        >
          <video
            height={24}
            className="absolute left-[2px] top-[3px] h-[24px] rounded-sm object-cover"
            src={segment.segment_metadata?.src}
            controls={false}
            autoPlay={false}
            muted={true}
            preload="metadata"
          />
        </div>
        <div className="relative m-[4px] flex h-full flex-col overflow-hidden rounded-[3px] bg-slate-100 px-[2px] py-px">
          <div
            className="truncate py-2 text-xs font-medium text-slate-500"
            title={getSegmentDisplayTextWithIntroOutroPrefix(segment)}
          >
            <div className="flex items-center gap-1">
              <span className="truncate">{truncateDisplayText(getSegmentDisplayText(segment))}</span>
              {isIntroOrOutroSegment && (
                <Badge data-testid="intro-outro-badge" variation="blue">
                  {segment.id === 'intro' ? 'Intro' : 'Outro'}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex h-full items-center justify-center text-slate-300">
            <Waveform />
          </div>
        </div>
      </div>
      {speakerTimelineContainerRef && !isTranslated && (
        <VideoSegmentTrimHandles
          fps={fps}
          clipId={clipId}
          clipData={clipData}
          segment={segment}
          showTrimsBars={showTrimHandlesOnHover}
          videoSegmentDragStart={videoSegmentDragStart || 0}
          videoSegmentDragEnd={videoSegmentDragEnd || 0}
          setVideoSegmentDragStart={setVideoSegmentDragStart}
          setVideoSegmentDragEnd={setVideoSegmentDragEnd}
          speakerTimelineContainerRef={speakerTimelineContainerRef}
        />
      )}
    </div>
  );
}

export default memo(VideoSegmentComponent);
